import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";
import { Loader2, PartyPopper } from "lucide-react";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [tickets, setTickets] = useState([]);
  const [event, setEvent] = useState(null);

  const poll = useCallback(
    async (attempt) => {
      if (attempt > 12) {
        setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const res = await api.get(`/tickets/by-session/${sessionId}`);
          setTickets(res.data.tickets || []);
          setEvent(res.data.event);
          setStatus("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (e) {}
      setTimeout(() => poll(attempt + 1), 2000);
    },
    [sessionId]
  );

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  if (status === "checking")
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <Loader2 className="animate-spin text-brand-orange" size={40} />
        <p className="font-mono text-sm uppercase tracking-widest text-brand-ink/60">Confirming your payment…</p>
      </div>
    );

  if (status !== "paid")
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="font-head text-3xl font-bold uppercase">We couldn't confirm that payment</p>
        <p className="text-brand-ink/60">If you were charged, email support@markie2fresh.com and we'll sort it out.</p>
        <Link to="/tickets" className="font-mono text-xs uppercase tracking-widest text-brand-orange mt-2">Try again →</Link>
      </div>
    );

  return (
    <div className="min-h-screen pt-32 pb-20 bg-brand-cream">
      <div className="max-w-3xl mx-auto px-5">
        <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-center" data-testid="payment-success">
          <div className="w-16 h-16 rounded-full bg-brand-orange mx-auto flex items-center justify-center">
            <PartyPopper size={28} className="text-white" />
          </div>
          <h1 className="font-head font-extrabold text-5xl md:text-7xl uppercase mt-6 leading-none">You're going!</h1>
          <p className="text-brand-ink/60 mt-3">
            {tickets.length} ticket{tickets.length > 1 ? "s" : ""} to <strong>{event?.title}</strong>. A copy is in your inbox.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-5 mt-12">
          {tickets.map((t) => (
            <Link
              key={t.qr_token}
              to={`/ticket/${t.qr_token}`}
              className="bg-white border border-brand-ink/10 p-6 flex items-center gap-5 hover:border-brand-orange transition-colors"
              data-testid={`success-ticket-${t.qr_token}`}
            >
              <div className="p-2 bg-white border border-brand-ink/10">
                <QRCodeSVG value={t.qr_token} size={80} fgColor="#0A0A0A" />
              </div>
              <div>
                <div className="font-head font-bold uppercase">{t.buyer_name}</div>
                <div className="font-mono text-[10px] text-brand-ink/40 mt-1">Tap to open full ticket →</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
